import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Maximize, Award, TrendingUp, Users, Zap, WifiOff, Volume2, VolumeX } from "lucide-react";
import { LiveDot } from "@/components/Panel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUG = "davangere-members-day-2026";
const FEED_TICK_MS = 800;
const RECONNECT_STEPS = [1000, 2000, 4000, 8000, 15000, 30000];
const APPLAUSE_SRC = "/applause.mp3";
const SOUNDTRACKS = {
  applause: "/applause.mp3",
  fanfare:  "/fanfare.mp3",
  dhol:     "/dhol.mp3",
  silent:   "",
};
// Replay lifecycle triggers (match the banner messages produced by the backend)
const REPLAY_START_RE = /replaying today'?s top/i;
const REPLAY_END_RE   = /that'?s a wrap|replay stopped/i;
const SFX_TAG_RE      = /\[sfx:(applause|fanfare|dhol|silent)\]\s*/i;

function rupees(paise) {
  if (!paise) return "₹0";
  const r = paise / 100;
  if (r >= 1e7) return `₹${(r / 1e7).toFixed(2)}Cr`;
  if (r >= 1e5) return `₹${(r / 1e5).toFixed(2)}L`;
  if (r >= 1e3) return `₹${(r / 1e3).toFixed(1)}K`;
  return `₹${r.toFixed(0)}`;
}

export default function BigScreen() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [snap, setSnap] = useState(null);
  const [connected, setConnected] = useState(false);
  const [banner, setBanner] = useState(null); // {message, ttl_seconds, until}
  const [clock, setClock] = useState(new Date());
  // --- Replay soundtrack state ---
  const audioRef = useRef(null);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [soundtrack, setSoundtrack] = useState("applause");
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("bni-bigscreen-sound") !== "0"; } catch { return true; }
  });
  const [needsUnlock, setNeedsUnlock] = useState(false);

  // Feed queue: never render >1 new feed item per 800ms.
  const feedQueue = useRef([]);
  const [feedShown, setFeedShown] = useState([]);
  const [backlog, setBacklog] = useState(0);
  const evtRef = useRef(null);
  const backoffRef = useRef(0);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const applySnapshot = (data) => {
    setSnap(data);
    setFeedShown(data.recent_feed || []);
    feedQueue.current = [];
    setBacklog(0);
  };

  const pushToFeed = (item) => {
    feedQueue.current.push(item);
    setBacklog(feedQueue.current.length);
  };

  // Drain feed queue at most once per FEED_TICK_MS. Pause when tab is hidden.
  useEffect(() => {
    let stop = false;
    const tick = () => {
      if (stop) return;
      if (!document.hidden && feedQueue.current.length > 0) {
        const next = feedQueue.current.shift();
        setBacklog(feedQueue.current.length);
        setFeedShown((prev) => [next, ...prev].slice(0, 20));
      }
      setTimeout(tick, FEED_TICK_MS);
    };
    tick();
    return () => { stop = true; };
  }, []);

  // Snap to current when tab returns to foreground.
  useEffect(() => {
    const onVis = () => {
      if (!document.hidden) {
        feedQueue.current = [];
        setBacklog(0);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const connect = useCallback(() => {
    if (evtRef.current) return;
    const es = new EventSource(`${API}/public/big-screen/${SLUG}/stream`);
    evtRef.current = es;

    es.addEventListener("open", () => { setConnected(true); backoffRef.current = 0; });
    es.addEventListener("snapshot", (e) => { try { applySnapshot(JSON.parse(e.data)); } catch {} });
    es.addEventListener("referral", (e) => { try { pushToFeed(JSON.parse(e.data)); } catch {} });
    es.addEventListener("round_change", (e) => {
      try {
        const d = JSON.parse(e.data);
        setSnap((s) => (s ? { ...s, current_round: d } : s));
      } catch {}
    });
    es.addEventListener("announcement", (e) => {
      try {
        const d = JSON.parse(e.data);
        let msg = d.message || "";
        // Pull out the soundtrack tag (if any) so the display stays clean
        const m = SFX_TAG_RE.exec(msg);
        if (m) {
          setSoundtrack(m[1].toLowerCase());
          msg = msg.replace(SFX_TAG_RE, "").trim();
        }
        setBanner({ message: msg, ttl_seconds: d.ttl_seconds, until: Date.now() + d.ttl_seconds * 1000 });
        // Soundtrack control — hooked to the opening / closing replay banners
        if (REPLAY_START_RE.test(msg)) setReplayPlaying(true);
        else if (REPLAY_END_RE.test(msg)) setReplayPlaying(false);
      } catch {}
    });
    es.addEventListener("ping", () => {});
    es.onerror = () => {
      setConnected(false);
      es.close();
      evtRef.current = null;
      const step = RECONNECT_STEPS[Math.min(backoffRef.current, RECONNECT_STEPS.length - 1)];
      backoffRef.current += 1;
      setTimeout(connect, step);
    };
  }, []);

  useEffect(() => {
    connect();
    return () => { evtRef.current?.close(); evtRef.current = null; };
  }, [connect]);

  // Auto-hide banner
  useEffect(() => {
    if (!banner) return;
    const iv = setInterval(() => {
      if (Date.now() > banner.until) setBanner(null);
    }, 500);
    return () => clearInterval(iv);
  }, [banner]);

  // --- Replay soundtrack: play/pause the loop in sync with the replay lifecycle ---
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const src = SOUNDTRACKS[soundtrack] || "";
    if (replayPlaying && soundOn && src) {
      if (!a.src.endsWith(src)) a.src = src;
      a.currentTime = 0; a.volume = 0; a.loop = true;
      const p = a.play();
      if (p?.catch) p.catch(() => setNeedsUnlock(true));
      // Fade-in over 600ms
      let v = 0;
      const fade = setInterval(() => {
        v = Math.min(0.85, v + 0.08);
        a.volume = v;
        if (v >= 0.85) clearInterval(fade);
      }, 60);
      return () => clearInterval(fade);
    }
    // Not playing — fade-out gracefully
    if (!a.paused) {
      let v = a.volume;
      const fade = setInterval(() => {
        v = Math.max(0, v - 0.1);
        a.volume = v;
        if (v <= 0) { a.pause(); clearInterval(fade); }
      }, 50);
      return () => clearInterval(fade);
    }
  }, [replayPlaying, soundOn, soundtrack]);

  useEffect(() => { try { localStorage.setItem("bni-bigscreen-sound", soundOn ? "1" : "0"); } catch {} }, [soundOn]);

  // One-tap unlock: on any click on the page, try to prime the audio element
  const unlockAudio = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    const p = a.play();
    if (p?.then) p.then(() => { a.pause(); a.currentTime = 0; setNeedsUnlock(false); }).catch(() => {});
  }, []);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) { document.exitFullscreen?.(); return; }
    el.requestFullscreen?.();
  };

  // Auto-fullscreen if ?auto=1
  useEffect(() => {
    if (params.get("auto") === "1") {
      setTimeout(() => { try { document.documentElement.requestFullscreen?.(); } catch {} }, 300);
    }
  }, [params]);

  const podium = (snap?.top_10_leaderboard || []).slice(0, 3);
  const runners = (snap?.top_10_leaderboard || []).slice(3);
  const activePct = snap?.total_tables ? Math.round((snap.active_tables / snap.total_tables) * 100) : 0;
  const bannerSecs = banner ? Math.max(0, Math.round((banner.until - Date.now()) / 1000)) : 0;

  return (
    <div className="min-h-screen w-full flex flex-col" style={{ background: "var(--bg)" }} data-testid="bigscreen"
         onClick={needsUnlock ? unlockAudio : undefined}>
      {/* Replay soundtrack (hidden; controlled by `replayPlaying`) */}
      <audio ref={audioRef} src={APPLAUSE_SRC} preload="auto" loop data-testid="bigscreen-applause-audio" />
      {replayPlaying && (
        <div className="w-full py-1.5 px-6 sm:px-10 flex items-center justify-center gap-2 text-xs mono-label"
             data-testid="bigscreen-replay-banner"
             style={{ background: "linear-gradient(90deg, rgba(226,62,71,0.14), rgba(226,62,71,0.28), rgba(226,62,71,0.14))",
                      color: "var(--text)", borderBottom: "1px solid rgba(226,62,71,0.4)" }}>
          <span style={{ animation: "pt-dot 1.2s ease-out infinite" }}>🏆</span>
          REPLAY · TOP 10 REFERRALS · {soundtrack.toUpperCase()} {soundOn ? "ON" : "MUTED"}
        </div>
      )}
      {needsUnlock && (
        <div className="w-full py-2 px-6 sm:px-10 text-center text-xs mono-label cursor-pointer"
             data-testid="bigscreen-unlock-audio"
             style={{ background: "var(--amber)", color: "#1a1a1a" }}>
          TAP ANYWHERE TO ENABLE APPLAUSE SOUNDTRACK
        </div>
      )}
      {banner && (
        <div className="w-full py-3 px-6 sm:px-10 flex items-center gap-4"
             data-testid="bigscreen-banner"
             style={{ background: "var(--red)", color: "var(--text)" }}>
          <Zap size={18} />
          <div className="text-2xl font-serif flex-1">{banner.message}</div>
          <div className="mono-label opacity-80">closes in {bannerSecs}s</div>
        </div>
      )}
      <div className="hairline-b flex items-center justify-between px-6 sm:px-10 py-4">
        <div className="flex items-center gap-4 min-w-0">
          <img src="/risse-bni-text.png" alt="RISSE 26 · BNI Davangere Members Day"
               className="hidden sm:block h-9 w-auto object-contain" style={{ maxWidth: "38vw" }} />
          <span className="hidden sm:inline-block w-px h-10" style={{ background: "rgba(247,244,238,0.16)" }} />
          <img src="/risse-bni-logo.png" alt="RISSE 26 landmarks" className="h-14 w-auto" />
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="bigscreen-sound-toggle"
            onClick={(e) => { e.stopPropagation(); setSoundOn((s) => !s); }}
            className="flex items-center gap-2 px-3 py-1.5 border border-[var(--hairline)] hover:border-[var(--red-2)] transition-colors mono-label text-xs"
            title={soundOn ? "Mute replay soundtrack" : "Unmute replay soundtrack"}
            style={{ color: soundOn ? "var(--green)" : "var(--taupe)" }}
          >
            {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
            {soundOn ? "SOUND ON" : "SOUND OFF"}
          </button>
          {!connected && <span className="chip" data-testid="bigscreen-offline" style={{ color: "var(--red-2)", borderColor: "var(--red-2)" }}><WifiOff size={12} /> reconnecting…</span>}
          {connected && <LiveDot />}
          <span className="mono-label" style={{ color: "var(--taupe)" }} data-testid="bigscreen-clock">{clock.toLocaleTimeString("en-IN", { hour12: false })}</span>
          <button data-testid="bigscreen-fullscreen-btn" onClick={goFullscreen} className="btn-ghost text-xs"><Maximize size={14} /> Fullscreen</button>
          <button onClick={() => nav("/")} className="btn-ghost text-xs"><ArrowLeft size={14} /> Exit</button>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-12 gap-6 p-6 sm:p-10">
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div className="grid grid-cols-3 gap-4" data-testid="bigscreen-kpis">
            <KPI k="Referrals logged" v={(snap?.referrals_logged ?? 0).toLocaleString("en-IN")} icon={<TrendingUp size={18} />} />
            <KPI k="Business volume" v={rupees(snap?.business_volume_paise ?? 0)} icon={<Award size={18} />} />
            <KPI k="Active tables" v={`${snap?.active_tables ?? 0}/${snap?.total_tables ?? 0}`} sub={`${activePct}%`} icon={<Users size={18} />} />
          </div>

          <div className="hairline p-6" data-testid="bigscreen-leaderboard">
            <div className="flex items-center justify-between">
              <div className="mono-label">TOP 10 GIVERS</div>
              {snap?.current_round?.round_number && (
                <div className="mono-label" style={{ color: "var(--taupe)" }}>Round {snap.current_round.round_number} · {snap.current_round.status}</div>
              )}
            </div>
            <div className="mt-6 grid grid-cols-3 gap-4">
              {podium.map((p, i) => (
                <motion.div key={p.registration_id} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                            className="hairline p-4" data-testid={`bigscreen-podium-${i + 1}`}
                            style={{ borderColor: ["var(--red)", "var(--taupe)", "var(--muted)"][i] || "var(--hairline)" }}>
                  <div className="mono-label" style={{ color: "var(--taupe)" }}>#{i + 1}</div>
                  <div className="font-serif text-3xl mt-1 truncate">{p.first_name}</div>
                  <div className="mono-label truncate">{p.chapter_short}</div>
                  <div className="mt-3 flex items-baseline justify-between">
                    <div className="font-mono text-2xl">{p.given_count}</div>
                    <div className="mono-label" style={{ color: "var(--taupe)" }}>{rupees(p.business_volume_paise)}</div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <AnimatePresence>
                {runners.map((p, i) => (
                  <motion.div key={p.registration_id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                              className="flex items-center gap-3 hairline p-2.5"
                              data-testid={`bigscreen-runner-${i + 4}`}>
                    <div className="mono-label" style={{ color: "var(--taupe)" }}>#{i + 4}</div>
                    <div className="flex-1 min-w-0 text-sm truncate">{p.first_name} · <span className="mono-label" style={{ color: "var(--taupe)" }}>{p.chapter_short}</span></div>
                    <div className="font-mono">{p.given_count}</div>
                    <div className="mono-label" style={{ color: "var(--taupe)" }}>{rupees(p.business_volume_paise)}</div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="hairline p-6" data-testid="bigscreen-feed">
            <div className="flex items-center justify-between">
              <div className="mono-label">LIVE FEED</div>
              {backlog > 5 && <span className="chip" data-testid="bigscreen-backlog" style={{ color: "var(--red-2)", borderColor: "var(--red-2)" }}>+{backlog} queued</span>}
            </div>
            <div className="mt-4 space-y-2 max-h-[70vh] overflow-hidden">
              <AnimatePresence initial={false}>
                {feedShown.map((f) => (
                  <motion.div key={f.id}
                              initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                              className="hairline p-3"
                              data-testid={`bigscreen-feed-${(f.id || "").toString().slice(0, 8)}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm truncate">
                        <span style={{ color: "var(--red)" }}>{f.from_first_name}</span>
                        <span className="mono-label mx-2" style={{ color: "var(--taupe)" }}>→</span>
                        {f.to_first_name}
                      </div>
                      <div className="mono-label" style={{ color: "var(--taupe)" }}>T{f.table_number} · R{f.round_number}</div>
                    </div>
                    {f.business_value_paise > 0 && (
                      <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{rupees(f.business_value_paise)}</div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ k, v, sub, icon }) {
  return (
    <div className="hairline p-5">
      <div className="flex items-center justify-between mono-label" style={{ color: "var(--taupe)" }}>{k}{icon}</div>
      <div className="mt-3 font-serif text-5xl leading-none">{v}</div>
      {sub && <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>{sub}</div>}
    </div>
  );
}
